import { useState } from "react";
import { ChartColumn, Table2 } from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { AiTable } from "./AiTable";
import { chartToTable, type ChartSeries, type ChartType } from "./chatMessageUtils";

const COLORS = ["#7c3aed", "#2563eb", "#059669", "#d97706", "#dc2626", "#0891b2", "#db2777", "#65a30d"];

const toRows = (series: ChartSeries[]) => {
  const rows = new Map<string, Record<string, string | number>>();
  for (const s of series) {
    for (const point of s.data) {
      const row = rows.get(point.label) ?? { label: point.label };
      row[s.name] = point.value;
      rows.set(point.label, row);
    }
  }
  return Array.from(rows.values());
};

const MAX_PIE_SLICES = 8;

export const groupPieSlices = (
  data: ChartSeries["data"],
  maxSlices: number = MAX_PIE_SLICES
): ChartSeries["data"] => {
  if (data.length <= maxSlices) return data;

  const sorted = [...data].sort((a, b) => b.value - a.value);
  const kept = sorted.slice(0, maxSlices - 1);
  const rest = sorted.slice(maxSlices - 1);
  const total = rest.reduce((sum, point) => sum + point.value, 0);

  return [...kept, { label: `Other (${rest.length} more)`, value: total }];
};

export const AiChart = ({
  chartType,
  title,
  series,
}: {
  chartType: ChartType;
  title?: string;
  series: ChartSeries[];
}) => {
  const rows = toRows(series);
  const pieData = chartType === "pie" ? groupPieSlices(series[0].data) : [];
  const [asTable, setAsTable] = useState(false);
  const tableData = chartToTable(series);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        {title ? <p className="font-semibold">{title}</p> : <span />}
        <button
          type="button"
          onClick={() => setAsTable((current) => !current)}
          className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs text-slate-500 transition-colors hover:bg-slate-200/70 hover:text-slate-800 focus-visible:outline-2 focus-visible:outline-slate-400"
          aria-label={asTable ? "View as chart" : "View as table"}
          aria-pressed={asTable}
        >
          {asTable ? <ChartColumn className="size-3.5" aria-hidden="true" /> : <Table2 className="size-3.5" aria-hidden="true" />}
          {asTable ? "View as chart" : "View as table"}
        </button>
      </div>
      {asTable ? (
        <AiTable title={title}>
          <thead>
            <tr>
              {tableData.columns.map((column, index) => (
                <th key={index} className="border-b border-slate-300 px-2 py-1 font-semibold">
                  {column}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {tableData.rows.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {row.map((cell, cellIndex) => (
                  <td key={cellIndex} className="border-b border-slate-200 px-2 py-1 align-top">
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </AiTable>
      ) : (
      <div className="h-64 w-full text-xs">
        <ResponsiveContainer width="100%" height="100%">
          {chartType === "pie" ? (
            <PieChart>
              <Pie data={pieData} dataKey="value" nameKey="label" outerRadius="80%">
                {pieData.map((_, index) => (
                  <Cell key={index} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          ) : chartType === "line" ? (
            <LineChart data={rows}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="label" />
              <YAxis />
              <Tooltip />
              {series.length > 1 ? <Legend /> : null}
              {series.map((s, index) => (
                <Line key={s.name} type="monotone" dataKey={s.name} stroke={COLORS[index % COLORS.length]} />
              ))}
            </LineChart>
          ) : (
            <BarChart data={rows}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="label" />
              <YAxis />
              <Tooltip />
              {series.length > 1 ? <Legend /> : null}
              {series.map((s, index) => (
                <Bar key={s.name} dataKey={s.name} fill={COLORS[index % COLORS.length]} />
              ))}
            </BarChart>
          )}
        </ResponsiveContainer>
      </div>
      )}
    </div>
  );
};
